// =============================================================================
// THE JOBS LAKE — B2 + M3 (docs/design/jobs-lake-design-2026-07-04.md,
// docs/diagrams/c3-jobs-lake-2026-07-04.svg). Ledger: docs/CHANGE-CONTROL.md
// R175 (his decisions) / R176 (the design doc this builds).
//
// THE BUG THIS FIXES (B2): jobs.canonical_url (jobs.ts:36) is globally
// UNIQUE, but jobs.user_id makes it a PER-USER table — a second user
// ingesting a URL a first user already has throws
// "duplicate key value violates unique constraint jobs_canonical_url_unique"
// and silently drops (live repro: 3 users share 186 Anthropic-board rows,
// later ingests failing). The fix is not a bigger composite unique
// (UNIQUE(user_id, canonical_url)) — that only stops the crash. His B2
// direction: the canonical URL should never have been the ROW'S IDENTITY.
// A posting is one fact in the world; N users watching it are N
// RELATIONSHIPS to that fact, not N copies of it.
//
// THREE LAYERS, ONE POSTING, MANY RELATIONSHIPS — generalizes the SAME
// shared/per-user split ats_registry (shared, no user_id) + watchlist_sources
// (per-user, registry_id FK) already proved at the BOARD level, one layer
// further in, at the LISTING level:
//
//   LAYER 1 · jobListings        — shared, no user_id. "We know this
//                                   posting exists." canonical_url stays
//                                   UNIQUE here — globally correct at THIS
//                                   layer (one row per real posting), unlike
//                                   at the per-user layer where it was the
//                                   bug.
//   LAYER 2 · jobListingDetails  — shared, no user_id, 1:1 with jobListings
//                                   (listingId is BOTH the PK and the FK —
//                                   not a surrogate). Created ONLY on
//                                   promotion to depth='ingested'. A SEPARATE
//                                   table (not nullable columns bolted onto
//                                   Layer 1) so "index-only, never ingested"
//                                   is a structural ABSENCE (no row), the
//                                   cheapest possible listing footprint —
//                                   never a row full of nulls.
//   LAYER 3 · userJobs           — per-user, userId uuid NOT NULL. The ONLY
//                                   table where a user and a posting meet.
//                                   UNIQUE(userId, listingId) is THE
//                                   composite key his B2 decision named
//                                   ("internal composite key... option (a)")
//                                   — it replaces the global canonical_url
//                                   unique for "has this user already got
//                                   this job," while placing ZERO constraint
//                                   on how many DIFFERENT users can each
//                                   have their own one link row to the same
//                                   listing. Zero collisions, by
//                                   construction, for any number of users.
//
// M3 rides the same shape: `depth` ('index' | 'ingested') on jobListings is
// the GraphQL-like fetch-depth flag — "index that a listing exists without
// ingesting what no user needs" (his words). A listing nobody's desiredRoles
// ever match sits at depth='index' forever: searchable by title/company for
// future scenario matching, never fully ingested (no wasted extract call, no
// wasted Claude scoring call, no wasted storage on a full JD nobody reads).
// Promotion (index -> ingested) is a pure INSERT into job_listing_details
// plus one UPDATE of the depth flag — never a rewrite of an existing wide
// row, never a migration.
//
// ADDITIVE-SCHEMA-ONLY (rule #11): three NEW tables. jobs / job_extractions /
// job_scores are completely untouched by this file — they keep serving every
// existing read/write path byte-for-byte. See docs/design/
// b2-m3-jobs-lake-neon.sql for the CREATE TABLE IF NOT EXISTS statements this
// mirrors (file-only; applied via the Neon tool, never drizzle-kit on the
// shared branch).
// =============================================================================

import { sql } from 'drizzle-orm';
import {
  boolean,
  customType,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** Same posture as jobs.ts's own `tsvector` customType (jobs.ts:25-29):
 *  Drizzle has no first-class tsvector builder, so this is a thin
 *  read-mirror. Declared WITHOUT `.generatedAlwaysAs()` on purpose — the
 *  actual GENERATED ALWAYS ... STORED expression lives ONLY in
 *  docs/design/b2-m3-jobs-lake-neon.sql, so drizzle-kit (not run on this
 *  branch) never attempts to emit or reconcile the generation expression. */
const tsvector = customType<{ data: unknown }>({
  dataType() {
    return 'tsvector';
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 1 · job_listings — shared, no user_id. "We know this posting exists."
// Written by ANY user's poll, discovery-email capture, extension click-
// capture, or company-discovery research pass that surfaces a posting —
// regardless of whether anyone needs the full JD yet.
// ─────────────────────────────────────────────────────────────────────────────
export const jobListings = pgTable(
  'job_listings',
  {
    /** The driving key — never the URL (his B2 direction, restated as a code
     *  invariant: nothing in this schema keys a relationship on canonical_url;
     *  it is a shared-fact dedup key ONLY, at this layer). */
    id: id(),
    /** Kept UNIQUE — but now the ONLY thing keyed on it, at the SHARED-fact
     *  layer where global uniqueness is actually correct: one posting URL =
     *  one real-world posting, full stop. This is the design doc's own
     *  framing (§1.1) of why global uniqueness here is NOT the bug — the bug
     *  was applying this same constraint one layer down, to a per-user fact. */
    canonicalUrl: text('canonical_url').notNull().unique(),
    /** greenhouse / lever / workday / adzuna / usajobs / rss_generic /
     *  seek_rss / discovery_email / extension. */
    source: varchar('source', { length: 32 }).notNull(),
    company: varchar('company', { length: 200 }).notNull(),
    title: varchar('title', { length: 200 }).notNull(),
    location: varchar('location', { length: 120 }),
    /** Which shared board this came from, when it came from one — mirrors
     *  watchlistSources.registryId's exact shape (watchlist.ts:56). Nullable:
     *  a listing surfaced by a non-registry path (adzuna/usajobs/paste/
     *  extension) carries no registry provenance, honestly. FK enforced at
     *  the SQL layer (ON DELETE SET NULL), not the Drizzle layer — this
     *  codebase's existing convention (see jobs.userId / watchlistSources.
     *  registryId's own doc comment: relations enforced in application code +
     *  a real FK in the SQL file, never guessed at the Drizzle layer alone). */
    registryId: uuid('registry_id'),
    /** 'index' | 'ingested' — the GraphQL-like fetch-depth flag (§2/§3 of the
     *  design doc). 'index': title/company/url/board only, no fetch, no
     *  extract, no score — "we know it's there." 'ingested': a
     *  jobListingDetails row exists (promoted, either synchronously by a
     *  targeted-first poll match, or later by the waitUntil tail / nightly
     *  cron sweep). Plain varchar (not a DB enum), matching this codebase's
     *  own convention elsewhere (atsRegistry.atsKind, watchlistSources.kind)
     *  so a future depth value is a data value, not a migration. */
    depth: varchar('depth', { length: 16 }).notNull().default('index'),
    /** When ANY poll first surfaced this URL. Immutable once set. */
    firstSeenAt: timestamp('first_seen_at', { withTimezone: true }).notNull(),
    /** Bumped every time ANY poll re-observes it — mirrors jobs.fetchedAt's
     *  "freshness is data" stance, one level up (jobs.ts:72-84's own doc
     *  comment). Distinct from firstSeenAt (immutable) and from
     *  createdAt/updatedAt (touched by any field edit). */
    lastSeenAt: timestamp('last_seen_at', { withTimezone: true }).notNull(),
    /** D-2 test-data flag (docs/BUILD-PLAN-2026-07-04.md, Reserved call 1) —
     *  same shape as jobs.isTestData, one layer over: stamped `true` only when
     *  the ACTING user (upsertListingByUrl's caller, jobsLake.ts) carried
     *  `account_kind IN ('demo','qa')` at write time. Defaults `false`. The
     *  ONE sanctioned read path (lib/testDataWall.ts) excludes
     *  `is_test_data=true` shared listings from a real viewer's reads; demo/qa
     *  viewers see everything. Since job_listings is a SHARED-fact table (no
     *  user_id — many users' user_jobs rows can point at the same listing),
     *  a listing only gets stamped true when its FIRST insert came from a
     *  demo/qa acting user — a real user's later poll of the same URL
     *  (upsertListingByUrl's ON CONFLICT DO UPDATE) never flips it back to
     *  false, matching the demo-seed-is-isolated intent (a real user simply
     *  never sees that row via the wall, regardless of who else later
     *  observed the same URL). */
    isTestData: boolean('is_test_data').notNull().default(false),
    /** QA #38/#39 cross-source dedup (Reserved call 2). When this listing is a
     *  NON-canonical duplicate of another real-world posting (same
     *  normalized company+title+location seen via a different source/URL — the
     *  Culture Amp role seen on adzuna + CareerOne + its own ATS), this points
     *  at the CANONICAL job_listings.id we kept (the richer row: ATS-direct >
     *  aggregator). NULL = this row IS canonical (or nothing was found to
     *  merge it with) — the overwhelming common case. NO HARD DELETE
     *  (no-deletion law): the duplicate row is KEPT, merely pointed at its
     *  canonical + EXCLUDED from user-facing reads (jobsLakeReads.ts's union
     *  filters `canonical_listing_id IS NULL`). Self-referential FK
     *  (job_listings.id) enforced at the SQL layer only (this codebase's
     *  convention — see registryId's own comment), ON DELETE SET NULL.
     *  Additive (rule #11), mirrored via docs/design/b1-pipeline-neon.sql. */
    canonicalListingId: uuid('canonical_listing_id'),
    ...timestamps,
  },
  (t) => ({
    sourceIdx: index('job_listings_source_idx').on(t.source),
    companyIdx: index('job_listings_company_idx').on(t.company),
    registryIdx: index('job_listings_registry_idx').on(t.registryId),
    /** Cross-source dedup: the read-side filter `WHERE canonical_listing_id IS
     *  NULL` (canonical-only, jobsLakeReads.ts) and the dedup-key company
     *  probe both scan by this + company. Backs both. */
    canonicalIdx: index('job_listings_canonical_idx').on(t.canonicalListingId),
    /** STALEST-FIRST promotion ordering (§2.4 of the design doc) — the
     *  nightly cron sweep orders `job_listings WHERE depth='index'` by
     *  firstSeenAt ASC, the same discipline registryFreshness.ts's
     *  orderStalestFirst already proves out one layer up. This index backs
     *  that scan directly. */
    depthFirstSeenIdx: index('job_listings_depth_first_seen_idx').on(t.depth, t.firstSeenAt),
  }),
);

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 2 · job_listing_details — shared, no user_id, 1:1 with job_listings.
// Created ONLY when depth is promoted to 'ingested'. A SEPARATE table (not
// nullable columns on job_listings) so "index-only, never ingested" is a
// structural ABSENCE (no row exists), not a row full of nulls — the cheapest
// possible listing footprint. See the design doc §1.4 for why three tables,
// not two: folding description into the shared listing unconditionally would
// force every listing to carry one the moment ANY user sees it, defeating
// M3's entire premise (index without ingesting).
// ─────────────────────────────────────────────────────────────────────────────
export const jobListingDetails = pgTable('job_listing_details', {
  /** 1:1, NOT a surrogate — this row's identity IS the listing's. PK and FK
   *  are the same column (mirrors the design doc's own §1.2 table exactly:
   *  "listing_id uuid PRIMARY KEY, FK -> job_listings.id"). */
  listingId: uuid('listing_id').primaryKey(),
  /** DEVIATION FROM THE DOC'S LITERAL §1.2 TABLE (flagged, not silent): a
   *  Postgres GENERATED ALWAYS column cannot reference another table's
   *  columns, so search_vector's generation expression (title(A)/company(B)/
   *  description(C)) needs title/company available ON THIS ROW. These two
   *  are read-only DENORMALIZED mirrors of job_listings.title/company for
   *  that generation expression's own use — never a second source of truth
   *  for a caller (a promotion always writes the SAME title/company
   *  job_listings already carries for this listingId; nothing reads these
   *  as authoritative — job_listings remains the one place a listing's
   *  title/company is edited). Nullable so the column ADD is safe against
   *  the (empty, pre-migration) table. See docs/design/
   *  b2-m3-jobs-lake-neon.sql's own note on this exact point. */
  title: varchar('title', { length: 200 }),
  company: varchar('company', { length: 200 }),
  workMode: varchar('work_mode', { length: 24 }),
  employmentType: varchar('employment_type', { length: 24 }),
  seniority: varchar('seniority', { length: 32 }),
  /** Kept as today's column TYPE + names for zero-friction carry-forward from
   *  jobs.salaryMinAud/salaryMaxAud (both integer there) — see the design doc
   *  §6 open question 3 on the AU-suffix naming debt (pre-existing, not
   *  introduced here; a separate later rename pass, not bundled into this
   *  structural migration). */
  salaryMinAud: integer('salary_min_aud'),
  salaryMaxAud: integer('salary_max_aud'),
  description: text('description'),
  requirements: jsonb('requirements').$type<string[]>(),
  sponsorshipMentioned: boolean('sponsorship_mentioned'),
  selectionCriteriaMode: boolean('selection_criteria_mode').default(false).notNull(),
  /** GENERATED ALWAYS ... STORED over title(A)/company(B)/description(C) —
   *  same generation expression as today's jobs.search_vector
   *  (w1-10-ats-search-neon.sql), just re-pointed at this table (and, per the
   *  deviation noted above, at this table's own denormalized title/company).
   *  Full-text search now runs over the SHARED repository, benefiting every
   *  user, not per-user copies of the same JD. See jobs.ts's own tsvector
   *  customType doc comment for why this is declared without
   *  .generatedAlwaysAs(). */
  searchVector: tsvector('search_vector'),
  /** When the promotion happened — distinct from job_listings.lastSeenAt
   *  (which tracks re-*sightings*, not re-*ingestions*). */
  ingestedAt: timestamp('ingested_at', { withTimezone: true }).notNull(),
  /** QA #40 (structured-ingestion) — the posting's OWN publish date, carried
   *  forward from a STRUCTURED source (adzuna `created`), same field + stance
   *  as jobs.postedAt: never derived/guessed, null when the source gives no
   *  parseable date. Additive (rule #11), mirrored via
   *  docs/design/b1-pipeline-neon.sql. */
  postedAt: timestamp('posted_at', { withTimezone: true }),
  /** Carries what job_extractions.extractionTier carries today, moved up a
   *  layer since the extraction is now itself a shared fact once one has
   *  happened. */
  extractionTier: varchar('extraction_tier', { length: 16 }),
  raw: jsonb('raw'),
});

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 3 · user_jobs — per-user, userId uuid NOT NULL. The ONLY place a user
// and a posting meet. The composite UNIQUE(userId, listingId) is THE
// composite key his B2 decision named ("internal composite key... as
// described in option (a)") — it replaces the global canonical_url unique
// entirely for "has this user already got this job."
// ─────────────────────────────────────────────────────────────────────────────
export const userJobs = pgTable(
  'user_jobs',
  {
    /** This user's own row identity. */
    id: id(),
    /** Per-user-uuid segment law — THIS table is where it lives (the design
     *  doc's own §1.3: "this is the ONLY place user_id (uuid) and a posting
     *  meet"). Native uuid column — distinct from every OTHER user_id column
     *  in this codebase today (jobs.userId, watchlistSources.userId,
     *  searchScenarios.userId, etc. are all `text('user_id')` holding a uuid
     *  STRING, a pre-existing convention documented as migration debt in
     *  CLAUDE.md: "text-keyed user_id tables are migration debt"). This new
     *  table is additive-only and never joins against those text columns via
     *  a typed FK, so choosing the correct native uuid type here does not
     *  require touching (and cannot break) any existing text-keyed table —
     *  it is the first table built AFTER that debt was named, so it is built
     *  right the first time rather than perpetuating the debt into a brand
     *  new table. users.id (users.ts:6, via the shared `id()` helper) is
     *  itself uuid, so this column's real values always match the
     *  authoritative type. */
    userId: uuid('user_id').notNull(),
    /** FK -> job_listings.id, ON DELETE CASCADE at the SQL layer (a listing
     *  row is never deleted by this design's own code — nothing-unrecoverable
     *  — but the constraint stays correct for the hypothetical). */
    listingId: uuid('listing_id').notNull(),
    /** LANE W2.1-BUCKETS (extends W1-4): 'home' | 'relocate' | 'bridge' |
     *  'dream' | 'pass' | 'unsorted' — computed per-user (bucket is a
     *  property of the job's facts scored against THIS user's own home
     *  country/locations/work-rights, unchanged semantics from jobs.bucket
     *  today — jobs.ts's own doc comment, carried forward verbatim). Nullable:
     *  undetermined/not-yet-scored, read exactly like 'unsorted'. */
    bucket: varchar('bucket', { length: 16 }),
    /** LANE W2.1-BUCKETS — 'auto' | 'user', the SAME terminal-override marker
     *  as jobs.bucketSource (see that column's doc comment for the full
     *  contract). Defaults 'auto'. */
    bucketSource: varchar('bucket_source', { length: 8 }).notNull().default('auto'),
    /** BOARD-TRUTH FIX — the SAME resolved-location marker as jobs.
     *  resolvedLocation (see that column's own doc comment for the full
     *  contract): the specific `;`-delimited location segment that justified
     *  a 'home'/'relocate' bucket for THIS user's link to the listing, null
     *  otherwise. */
    resolvedLocation: varchar('resolved_location', { length: 160 }),
    /** {kind, sourceId, at} — this user's own discovery provenance, exactly
     *  jobs.discoveredAt's shape today. */
    discoveredAt: jsonb('discovered_at'),
    closedAt: jsonb('closed_at'),
    /** This user's watchlistSources row id that surfaced it for THEM — a
     *  DIFFERENT user may have a different sourceId pointing at the SAME
     *  listingId (this is the whole point: N users, N relationships, one
     *  shared fact). No FK at the Drizzle layer (matches jobs.userId /
     *  watchlistSources.registryId's own documented convention: relations in
     *  application code, not the schema). */
    sourceId: text('source_id'),
    /** D-2 test-data flag (docs/BUILD-PLAN-2026-07-04.md, Reserved call 1) —
     *  this LINK row's own stamp: `true` only when THIS user (the one who
     *  owns this user_jobs row) carried `account_kind IN ('demo','qa')` at
     *  the moment the link was created (upsertUserJobLink's caller). Kept
     *  independent of job_listings.isTestData on purpose — a demo user
     *  linking to an otherwise-real shared listing still marks THEIR OWN
     *  relationship row as test data (so a later real-user reuse of the same
     *  listing is unaffected), while a real user could theoretically link to
     *  a demo-seeded listing (their own link row stays false; the wall's
     *  actual protection for a real viewer is the job_listings-level stamp,
     *  this column covers the per-relationship half of the fine-grained
     *  law). Defaults `false`. */
    isTestData: boolean('is_test_data').notNull().default(false),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('user_jobs_user_idx').on(t.userId),
    listingIdx: index('user_jobs_listing_idx').on(t.listingId),
    /** The composite key. Uniqueness is enforced as a real table-level UNIQUE
     *  constraint in the SQL file (Drizzle's per-column `.unique()` cannot
     *  express a composite pair — same posture ats_registry.ts's own doc
     *  comment documents for its (ats_kind, token) pair); this index exists
     *  for the per-user-listing lookup read pattern (the equivalent of
     *  jobs.ts's own userIdx/bucketIdx composite reads). */
    userListingIdx: index('user_jobs_user_listing_idx').on(t.userId, t.listingId),
    bucketIdx: index('user_jobs_bucket_idx').on(t.userId, t.bucket),
  }),
);

// ─────────────────────────────────────────────────────────────────────────────
// combine_provenance — the COMBINE plan (docs/design/
// multi-user-same-job-combine-plan-2026-07-04.md §4.4), Phase 1. Shared,
// no user_id, 1:1 with job_listings — additive-only (rule #11), mirrored in
// docs/design/combine-phases-1-3-neon.sql.
//
// WHY A SEPARATE TABLE, NOT COLUMNS ON job_listing_details: same reasoning as
// Layer 2's own split from Layer 1 (this file's own header) — provenance is a
// structural ADDITION that exists only for a listing that has actually
// absorbed a second source's facts. Its absence (no row) is the honest state
// for a never-combined listing, never a row full of nulls. jsonb keeps the
// per-field record open (one entry per combined field) without a
// column-per-field migration every time a new combinable field is added.
//
// NO-INVENTION IN CODE: the only writer of this table is
// combineDetails() (apps/api/lib/crossSourceDedup.ts) — a pure function that
// can only ever (a) copy a REAL observed value from one of two real listing
// rows, or (b) leave a field null. It never averages, blends, or
// synthesizes a value. A losing value in a conflict is recorded in
// `conflicts[]`, never silently dropped — see combineDetails's own doc
// comment + its NO-INVENTION test.
// ─────────────────────────────────────────────────────────────────────────────
export const combineProvenance = pgTable('combine_provenance', {
  /** 1:1 with the CANONICAL job_listings row this provenance describes — NOT
   *  a surrogate, mirrors job_listing_details.listingId's own PK=FK shape. */
  listingId: uuid('listing_id').primaryKey(),
  /** Per-field record — see the design doc §4.4 for the exact shape:
   *  { [field]: { value, source, sourceListingId, corroborations, conflicts:
   *  [{value, source, sourceListingId}], at } }. One entry per field that has
   *  EVER been through a combine decision for this canonical (present with
   *  corroborations=1/conflicts=[] even the FIRST time a field is filled from
   *  a single source, so "has this field been combine-touched" is answerable
   *  without a second table). */
  fields: jsonb('fields').notNull().default(sql`'{}'::jsonb`),
  updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().default(sql`now()`),
});
